import type { SubtractionKeepChallengeConfig } from './SubtractionKeepConfig.js';

export type SubtractionKeepActionResult =
  | 'accepted'
  | 'ready'
  | 'complete'
  | 'needs-decomposition'
  | 'already-decomposed'
  | 'nothing-to-remove'
  | 'not-ready';

export interface SubtractionKeepSerializedState {
  readonly version: 1;
  readonly remainingTens: number;
  readonly remainingOnes: number;
  readonly removedTens: number;
  readonly removedOnes: number;
  readonly decomposed: boolean;
  readonly hintStage: number;
  readonly complete: boolean;
}

export interface SubtractionKeepSnapshot extends SubtractionKeepSerializedState {
  readonly currentValue: number;
  readonly requiredTensToRemove: number;
  readonly requiredOnesToRemove: number;
  readonly canCheck: boolean;
  readonly hint?: string;
}

export class SubtractionKeepState {
  private remainingTens: number;
  private remainingOnes: number;
  private removedTens = 0;
  private removedOnes = 0;
  private decomposed = false;
  private hintStage = 0;
  private completed = false;

  constructor(
    readonly config: SubtractionKeepChallengeConfig,
    saved?: Partial<SubtractionKeepSerializedState>,
  ) {
    this.remainingTens = Math.floor(config.minuend / 10);
    this.remainingOnes = config.minuend % 10;
    if (saved?.version === 1) this.restore(saved);
  }

  decomposeTen(): SubtractionKeepActionResult {
    if (this.completed) return 'complete';
    if (this.decomposed) return 'already-decomposed';
    if (this.remainingTens < 1) return 'nothing-to-remove';
    this.remainingTens -= 1;
    this.remainingOnes += 10;
    this.decomposed = true;
    return 'accepted';
  }

  removeOne(): SubtractionKeepActionResult {
    if (this.completed) return 'complete';
    const target = this.config.subtrahend % 10;
    if (this.removedOnes >= target) return 'nothing-to-remove';
    if (this.remainingOnes < 1) return 'needs-decomposition';
    this.remainingOnes -= 1;
    this.removedOnes += 1;
    return this.canCheck() ? 'ready' : 'accepted';
  }

  removeTen(): SubtractionKeepActionResult {
    if (this.completed) return 'complete';
    const target = Math.floor(this.config.subtrahend / 10);
    if (this.removedTens >= target || this.remainingTens < 1) return 'nothing-to-remove';
    this.remainingTens -= 1;
    this.removedTens += 1;
    return this.canCheck() ? 'ready' : 'accepted';
  }

  check(): SubtractionKeepActionResult {
    if (!this.canCheck()) return 'not-ready';
    this.completed = this.remainingTens * 10 + this.remainingOnes === this.config.difference;
    return this.completed ? 'complete' : 'not-ready';
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.snapshot.hint;
  }

  serialize(): SubtractionKeepSerializedState {
    return {
      version: 1,
      remainingTens: this.remainingTens,
      remainingOnes: this.remainingOnes,
      removedTens: this.removedTens,
      removedOnes: this.removedOnes,
      decomposed: this.decomposed,
      hintStage: this.hintStage,
      complete: this.completed,
    };
  }

  get snapshot(): SubtractionKeepSnapshot {
    return {
      ...this.serialize(),
      currentValue: this.remainingTens * 10 + this.remainingOnes,
      requiredTensToRemove: Math.floor(this.config.subtrahend / 10),
      requiredOnesToRemove: this.config.subtrahend % 10,
      canCheck: this.canCheck(),
      hint: this.hintStage > 0 ? this.config.hints[this.hintStage - 1] : undefined,
    };
  }

  private canCheck() {
    return this.removedTens === Math.floor(this.config.subtrahend / 10)
      && this.removedOnes === this.config.subtrahend % 10;
  }

  private restore(saved: Partial<SubtractionKeepSerializedState>) {
    const integers = [saved.remainingTens, saved.remainingOnes, saved.removedTens, saved.removedOnes];
    if (!integers.every(value => Number.isInteger(value) && Number(value) >= 0)) return;
    this.remainingTens = Number(saved.remainingTens);
    this.remainingOnes = Number(saved.remainingOnes);
    this.removedTens = Number(saved.removedTens);
    this.removedOnes = Number(saved.removedOnes);
    this.decomposed = saved.decomposed === true;
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.completed = saved.complete === true
      && this.remainingTens * 10 + this.remainingOnes === this.config.difference;
  }
}
